/**
 * Cache Manager for optimizing data fetching and reducing load times
 * Implements memory cache with TTL (Time To Live) and localStorage persistence
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  defaultTTL: number; // Default TTL in milliseconds
  maxMemoryItems: number; // Maximum items to keep in memory
  persistToLocalStorage: boolean;
}

class CacheManager {
  private memoryCache = new Map<string, CacheItem<any>>();
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutes default
      maxMemoryItems: 100,
      persistToLocalStorage: true,
      ...config,
    };
  }

  /**
   * Generate a cache key based on multiple parameters
   */
  generateKey(prefix: string, ...params: (string | number | boolean | undefined | null)[]): string {
    const cleanParams = params.filter(p => p !== undefined && p !== null);
    return `${prefix}:${cleanParams.join(':')}`;
  }

  /**
   * Set data in cache with optional TTL
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.defaultTTL,
    };

    // Store in memory cache
    this.memoryCache.set(key, cacheItem);

    // Persist to localStorage if enabled
    if (this.config.persistToLocalStorage) {
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
      } catch (error) {
        console.warn('Failed to persist cache to localStorage:', error);
      }
    }

    // Clean up old items if memory cache is too large
    this.cleanupMemoryCache();
  }

  /**
   * Get data from cache, checking both memory and localStorage
   */
  get<T>(key: string): T | null {
    // Check memory cache first
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && this.isValid(memoryItem)) {
      return memoryItem.data;
    }

    // Check localStorage if memory cache miss
    if (this.config.persistToLocalStorage) {
      try {
        const stored = localStorage.getItem(`cache_${key}`);
        if (stored) {
          const cacheItem: CacheItem<T> = JSON.parse(stored);
          if (this.isValid(cacheItem)) {
            // Restore to memory cache
            this.memoryCache.set(key, cacheItem);
            return cacheItem.data;
          } else {
            // Remove expired item from localStorage
            localStorage.removeItem(`cache_${key}`);
          }
        }
      } catch (error) {
        console.warn('Failed to read cache from localStorage:', error);
      }
    }

    return null;
  }

  /**
   * Check if cached data exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Remove specific item from cache
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
    if (this.config.persistToLocalStorage) {
      localStorage.removeItem(`cache_${key}`);
    }
  }

  /**
   * Clear all cache data
   */
  clear(): void {
    this.memoryCache.clear();
    if (this.config.persistToLocalStorage) {
      // Remove all cache items from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('cache_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * Clear cache for a specific prefix
   */
  clearByPrefix(prefix: string): void {
    // Clear from memory cache
    const keysToDelete: string[] = [];
    this.memoryCache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.memoryCache.delete(key));

    // Clear from localStorage
    if (this.config.persistToLocalStorage) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`cache_${prefix}`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  /**
   * Get or fetch data with caching
   */
  async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Try to get from cache first
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Fetch fresh data
    const data = await fetchFn();
    
    // Cache the result
    this.set(key, data, ttl);
    
    return data;
  }

  /**
   * Check if cache item is still valid
   */
  private isValid<T>(item: CacheItem<T>): boolean {
    return Date.now() - item.timestamp < item.ttl;
  }

  /**
   * Clean up memory cache to prevent memory leaks
   */
  private cleanupMemoryCache(): void {
    if (this.memoryCache.size <= this.config.maxMemoryItems) {
      return;
    }

    // Convert to array and sort by timestamp (oldest first)
    const items = Array.from(this.memoryCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    );

    // Remove oldest items until we're under the limit
    const itemsToRemove = items.slice(0, items.length - this.config.maxMemoryItems);
    itemsToRemove.forEach(([key]) => {
      this.memoryCache.delete(key);
    });
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      memoryItems: this.memoryCache.size,
      maxMemoryItems: this.config.maxMemoryItems,
      defaultTTL: this.config.defaultTTL,
    };
  }
}

// Create different cache instances for different data types
export const repairCache = new CacheManager({
  defaultTTL: 10 * 60 * 1000, // 10 minutes for repairs
  maxMemoryItems: 50,
});

export const stockCache = new CacheManager({
  defaultTTL: 15 * 60 * 1000, // 15 minutes for stock data
  maxMemoryItems: 100,
});

export const userCache = new CacheManager({
  defaultTTL: 30 * 60 * 1000, // 30 minutes for user data
  maxMemoryItems: 20,
});

// Export the CacheManager class for custom instances
export { CacheManager };
